/**
 * Cálculo de cuotas de ares2010.validarcuotas — misma fórmula que el backend
 * (_calcular en ventaproductosbackend.py). Extraído de ventaproductos.js para
 * poder reutilizarlo en pantallas que simulan un plan de crédito sin vender
 * (cotizaciones).
 */

import { useEffect, useState } from "react";
import { useApi } from "lib/api";

// Cuotas por mes de cada forma de pago.
export const FACTOR_PERIODO = { Meses: 1, Quincenas: 2, Semanas: 4, Dias: 30 };

export const FORMAS_PAGO = Object.keys(FACTOR_PERIODO);

/** Sube la cuota al peso siguiente (varinicial.redondeocuota). */
export function redondearCuota(valor) {
  // toFixed(6) evita que 1500.0000001 suba a 1501 por ruido de float
  return Math.ceil(Number(valor.toFixed(6)));
}

/**
 * redondear (varinicial.redondeocuota = 'S'): la cuota sube al peso y la
 * diferencia va al interés, igual que el backend.
 *
 * siempreTasa: el interés es siempre % mensual, también de 20 en adelante.
 * Sin él rige la regla del escritorio (>= 20 = monto total de interés).
 * Préstamos personales lo usa: allí pasar del 20% es legítimo con
 * autorización, y con la regla vieja un 25% se volvía RD$25 de interés.
 */
export function calcularCuotas(
  financia,
  plazo,
  formadepago,
  interes,
  redondear = false,
  { siempreTasa = false } = {}
) {
  const factor = FACTOR_PERIODO[formadepago];
  if (!factor || financia <= 0 || plazo <= 0 || interes <= 0) return null;
  let interesMensual = interes;
  if (!siempreTasa && interesMensual >= 20) {
    // El escritorio interpreta >= 20 como monto total de interés.
    interesMensual = (((interesMensual / plazo) * factor) / financia) * 100;
  }
  const porinteres = interesMensual / 100 / factor;
  const cuota = (financia * porinteres * plazo + financia) / plazo;
  const valorcuota = redondear ? redondearCuota(cuota) : cuota;
  return { valorcuota, deudatotal: valorcuota * plazo };
}

/**
 * Inverso de calcularCuotas para el redondeo de cuotas: el usuario digita la
 * cuota y sale el interés mensual que la produce. Devuelve
 * { interes: "4.065541" } o { error } si la cuota no sirve.
 *
 * La tasa se trunca (no redondea) a 6 decimales: así la cuota recalculada
 * queda apenas por debajo de la digitada y el ceil del redondeo, aquí y en
 * el backend (_calcular), la devuelve exacta. Una cuota con centavos sube al
 * peso siguiente, igual que cualquier cuota con redondeo.
 */
export function interesDesdeCuota(
  financia,
  plazo,
  formadepago,
  cuota,
  { siempreTasa = false } = {}
) {
  const factor = FACTOR_PERIODO[formadepago];
  if (!factor || !(financia > 0) || !(plazo > 0)) {
    return { error: "Indique el monto a financiar, el plazo y la forma de pago" };
  }
  if (!(cuota > 0)) return { error: "Digite el valor de la cuota" };
  const minima = financia / plazo;
  if (cuota <= minima + 0.000001) {
    return { error: `La cuota debe ser mayor que RD$ ${minima.toFixed(2)} (solo capital)` };
  }
  const mensual = ((cuota - minima) / financia) * factor * 100;
  // >= 20 el cálculo lo toma como monto total de interés, no como tasa.
  if (!siempreTasa && mensual >= 20)
    return { error: "La cuota resulta en un interés de 20% mensual o más" };
  const truncado = Math.floor(mensual * 1e6) / 1e6;
  return { interes: String(Number(truncado.toFixed(6))) };
}

/** Fecha de mañana (yyyy-mm-dd, hora local): mínimo de la primera cuota. */
export function mananaISO() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * Tope de la primera cuota: un mes desde hoy (mismo día del mes siguiente; el
 * 31 cae al último día de un mes corto). Igual que _un_mes_despues del backend.
 */
export function unMesISO() {
  const hoy = new Date();
  const ultimo = new Date(hoy.getFullYear(), hoy.getMonth() + 2, 0).getDate();
  const d = new Date(hoy.getFullYear(), hoy.getMonth() + 1, Math.min(hoy.getDate(), ultimo));
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * Opciones de cuotas del tenant (varinicial): redondeo al peso y fecha de la
 * primera cuota digitable al crear el préstamo. Ambas en false mientras carga
 * o si falla (el backend valida y recalcula igual al guardar).
 */
export function useConfigCuotas() {
  const { post } = useApi();
  const [config, setConfig] = useState({ redondeo: false, elegirPrimeraCuota: false });
  useEffect(() => {
    let vivo = true;
    post("/api/config_cuotas", {}).then(({ ok, data }) => {
      if (vivo && ok) {
        setConfig({
          redondeo: data.redondeocuota === "S",
          elegirPrimeraCuota: data.elegirprimeracuota === "S",
        });
      }
    });
    return () => {
      vivo = false;
    };
  }, [post]);
  return config;
}

export default calcularCuotas;
